import { Delete } from '@mui/icons-material';
import { Alert, Autocomplete, Box, Button, CircularProgress, IconButton, List, ListItem, ListItemText, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';

const OrganizationMembers = () => {
  const { currentOrg } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteUid, setInviteUid] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [userOptions, setUserOptions] = useState([]);

  useEffect(() => {
    if (currentOrg) fetchMembers();
  }, [currentOrg]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/organizations/users');
      setMembers(res.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Не удалось загрузить участников');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserOptions = async (query) => {
    if (!query) {
      setUserOptions([]);
      return;
    }
    try {
      const res = await apiClient.get(`/auth/users?search=${encodeURIComponent(query)}`);
      setUserOptions(res.data);
    } catch (err) {
      console.error('Ошибка поиска пользователей', err);
    }
  };

  const handleInvite = async () => {
    if (!inviteUid) return;
    setLoading(true);
    try {
      const res = await apiClient.post('/organizations/users', { uid: inviteUid, role: inviteRole });
      setMembers(prev => [...prev, res.data]);
      setInviteUid('');
      setError('');
    } catch (err) {
      console.error(err);
      setError('Ошибка приглашения');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (uid, role) => {
    try {
      const res = await apiClient.patch(`/organizations/users/${uid}`, { role });
      setMembers(prev => prev.map(m => m.uid === uid ? res.data : m));
      setError('');
    } catch (err) {
      console.error(err);
      setError('Ошибка смены роли');
    }
  };

  const handleRemove = async (uid) => {
    try {
      await apiClient.delete(`/organizations/users/${uid}`);
      setMembers(prev => prev.filter(m => m.uid !== uid));
      setError('');
    } catch (err) {
      console.error(err);
      setError('Ошибка удаления участника');
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>Участники организации</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Autocomplete
          freeSolo
          options={userOptions}
          getOptionLabel={(option) => option.email || ''}
          onInputChange={(_, value) => {
            setInviteUid(value);
            fetchUserOptions(value);
          }}
          onChange={(_, option) => {
            if (typeof option === 'string') {
              setInviteUid(option);
            } else {
              setInviteUid(option ? option.id : '');
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="UID или Email" />
          )}
          sx={{ width: 250 }}
        />
        <Select value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
          <MenuItem value="super_admin">Super Admin</MenuItem>
          <MenuItem value="viewer">Viewer</MenuItem>
          <MenuItem value="manager">Manager</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
        </Select>
        <Button variant="contained" onClick={handleInvite} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Пригласить'}
        </Button>
      </Box>
      <List>
        {members.map(member => (
          <ListItem key={member.uid} secondaryAction={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Select value={member.role} onChange={e => handleRoleChange(member.uid, e.target.value)} size="small">
                <MenuItem value="super_admin">Super Admin</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
              <IconButton onClick={() => handleRemove(member.uid)}><Delete /></IconButton>
            </Box>
          }>
            <ListItemText primary={member.uid} secondary={member.role} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default OrganizationMembers; 